import { useCallback, useState } from "react";
import { AxiosError } from "axios";

// Api
import { ChangeEmail } from "../../Api/Account";

type UseEmailUpdateArgs = {
    getValidAccessToken: () => Promise<string | null>;
};

type EmailUpdateResult =
    | { ok: true }
    | { ok: false; error?: AxiosError | Error; status?: number };

export function useUpdateEmail({ getValidAccessToken }: UseEmailUpdateArgs) {
    const [isUpdating, setIsUpdating] = useState(false);
    const [lastErrorMessage, setLastErrorMessage] = useState<string | null>(null);

    const updateEmail = useCallback(
        async (newEmail: string): Promise<EmailUpdateResult> => {
            const token = await getValidAccessToken();
            if (!token) {
                const err = new Error("Not authenticated");
                setLastErrorMessage(err.message);
                return { ok: false, error: err };
            }

            const controller = new AbortController();
            setIsUpdating(true);
            setLastErrorMessage(null);

            try {
                const res = await ChangeEmail(newEmail, token, controller);

                if (res === true) {
                    return { ok: true };
                }

                const axiosErr = res as AxiosError;
                let msg = "E-mail failed to update.";
                let status: number | undefined;

                if (axiosErr.response) {
                    status = axiosErr.response.status;

                    // backend may return a string or { error: "..." }
                    const data = axiosErr.response.data as any;
                    if (typeof data === "string") {
                        msg = data;
                    } else if (data && typeof data.error === "string") {
                        msg = data.error;
                    }

                    if (status === 409) {
                        msg = "That e-mail address is already in use.";
                    } else if (status === 401) {
                        msg = "Your session has expired. Please log in again.";
                    }
                }

                setLastErrorMessage(msg);
                return { ok: false, error: axiosErr, status };
            } catch (err) {
                setLastErrorMessage("E-mail failed to update.");
                return { ok: false, error: err as Error };
            } finally {
                controller.abort();
                setIsUpdating(false);
            }
        },
        [getValidAccessToken]
    );

    const clearEmailError = useCallback(() => setLastErrorMessage(null), []);

    return {
        updateEmail,
        isUpdating,
        lastErrorMessage,
        clearEmailError,
    };
}
